import type { FC } from 'react'
import { useRef, useState } from 'react'

import { Button } from '@app/components/ui/button'
import {
  DialogActionTrigger,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle
} from '@app/components/ui/dialog'
import { useMetricSourceDeleteMutation } from '@graphql/queries'

interface Props {
  metricSourceId?: string
  onDelete?: () => void
}

const MetricSourceDeleteButton: FC<Props> = ({ metricSourceId = null, onDelete = () => {} }) => {
  const [open, setOpen] = useState(false)
  const cancelRef = useRef(null)
  const [loading, setLoading] = useState(false)

  const [, metricSourceDelete] = useMetricSourceDeleteMutation()

  const deleteMetricSource = (clearExistingData: boolean) => {
    if (!metricSourceId) {
      return false
    }

    setOpen(false)
    setLoading(true)
    return metricSourceDelete({ input: { metricSourceId, clearExistingData } })
      .then(() => {
        onDelete()
        return true
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Button aria-label="Disconnect metric source" colorPalette="red" loading={loading} onClick={() => setOpen(true)}>
        Disconnect
      </Button>

      <DialogRoot
        initialFocusEl={cancelRef?.current}
        onOpenChange={(e) => setOpen(e.open)}
        open={open}
        placement="center"
        role="alertdialog"
      >
        <DialogBackdrop />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect metric source</DialogTitle>
          </DialogHeader>

          <DialogBody>Are you sure? You can&#39;t undo this action afterwards.</DialogBody>

          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button ref={cancelRef} variant="outline">
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button ml={3} onClick={() => deleteMetricSource(false)} variant="outline">
              Keep Data
            </Button>
            <Button ml={3} bgColor="error" onClick={() => deleteMetricSource(true)}>
              Discard Data
            </Button>
          </DialogFooter>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </>
  )
}

export default MetricSourceDeleteButton
